import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function PreCadastrosPage() {
  const [alunos, setAlunos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aprovando, setAprovando] = useState(null);
  const [copiandoLink, setCopiandoLink] = useState(false);

  const [form, setForm] = useState({});

  const fetchData = async () => {
    try {
      const [pre, tur, pla] = await Promise.all([
        api.get('/gestor/pre-cadastros'),
        api.get('/gestor/turmas'),
        api.get('/gestor/planos')
      ]);

      setAlunos(pre.data);
      setTurmas(tur.data);
      setPlanos(pla.data);
    } catch {
      toast.error('Erro ao carregar pré-cadastros');
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const copiarLink = async () => {
    if (copiandoLink) return;

    setCopiandoLink(true);

    try {
      const resposta = await api.get('/gestor/pre-cadastros/link');
      const url = resposta.data?.url;

      if (!url) {
        throw new Error('Link nao disponivel');
      }

      await navigator.clipboard.writeText(url);
      toast.success('Link de pré-cadastro copiado!');
    } catch (erro) {
      toast.error(
        'Não foi possível copiar o link. Verifique as permissões do navegador.'
      );
    } finally {
      setCopiandoLink(false);
    }
  };

  const setCampo = (alunoId, campo, valor) => {
    setForm(prev => ({
      ...prev,
      [alunoId]: {
        ...(prev[alunoId] || {}),
        [campo]: valor
      }
    }));
  };

  const aprovarAluno = async (id) => {
    const dados = form[id] || {};

    if (!dados.turma_id || !dados.plano_id || !dados.dia_vencimento) {
      toast.error('Selecione turma, plano e vencimento');
      return;
    }

    try {
      setAprovando(id);

      const resposta = await api.put(`/gestor/pre-cadastros/${id}/aprovar`, {
        turma_id: dados.turma_id,
        plano_id: dados.plano_id,
        dia_vencimento: Number(dados.dia_vencimento),
        tipo_primeira_cobranca: dados.tipo_primeira_cobranca || 'inscricao',
        valor_primeira_cobranca:
          dados.valor_primeira_cobranca === '' ||
          dados.valor_primeira_cobranca == null
            ? null
            : Number(dados.valor_primeira_cobranca),
        status_primeira_mensalidade: dados.status_primeira_mensalidade || 'aguardando_pagamento'
      });

      toast.success('Aluno aprovado! Acesso liberado com a senha padrão.');

      if (resposta.data?.aviso_responsavel) {
        toast.error(resposta.data.aviso_responsavel, {
          duration: 8000
        });
      }

      setAlunos(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao aprovar aluno');
    }

    setAprovando(null);
  };

  return (
    <div className="space-y-6">

      <div>
        <h1 className="text-3xl font-black uppercase text-white">
          Pré-cadastros
        </h1>

        <p className="text-zinc-400">
          Configure turma, plano e vencimento antes de aprovar o aluno.
        </p>
      </div>

      <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-5">
        <h2 className="text-lg font-bold text-white mb-2">
          Link de pré-cadastro da escola
        </h2>
        <p className="text-zinc-400 text-sm mb-4">
          Compartilhe este link com os interessados em se cadastrar.
        </p>
        <button
          type="button"
          onClick={copiarLink}
          disabled={copiandoLink}
          className="bg-[#007AFF] hover:bg-[#0066DD] disabled:opacity-50 text-white px-5 h-11 rounded-xl font-bold"
        >
          {copiandoLink ? 'Copiando...' : 'Copiar link'}
        </button>
      </div>



      {loading ? (
        <div className="text-zinc-500">Carregando...</div>
      ) : alunos.length === 0 ? (
        <div className="text-zinc-500">Nenhum pré-cadastro encontrado.</div>
      ) : (
        <div className="grid gap-4">
          {alunos.map((a) => (
            <div
              key={a.id}
              className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 space-y-5"
            >
              <div>
                <h2 className="text-xl font-bold text-white">{a.name}</h2>
                <p className="text-zinc-400 text-sm">{a.email}</p>
                <p className="text-zinc-400 text-sm">{a.phone}</p>
                <p className="text-zinc-400 text-sm">
                  Responsável: {a.responsavel_nome || '-'}
                </p>
                <p className="text-zinc-400 text-sm">
                  WhatsApp responsável: {a.responsavel_whatsapp || '-'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
                <select
                  value={form[a.id]?.turma_id || ''}
                  onChange={(e) => setCampo(a.id, 'turma_id', e.target.value)}
                  className="h-11 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-white px-3"
                >
                  <option value="">Selecionar turma</option>
                  {turmas.map(t => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                  ))}
                </select>

                <select
                  value={form[a.id]?.plano_id || ''}
                  onChange={(e) => {
                    const planoId = e.target.value;
                    const planoSelecionado = planos.find(p => p.id === planoId);

                    setForm(prev => ({
                      ...prev,
                      [a.id]: {
                        ...(prev[a.id] || {}),
                        plano_id: planoId,
                        valor_primeira_cobranca:
                          planoId && planoSelecionado
                            ? Number(planoSelecionado.valor || 0).toFixed(2)
                            : ''
                      }
                    }));
                  }}
                  className="h-11 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-white px-3"
                >
                  <option value="">Selecionar plano</option>
                  {planos.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.nome} - R$ {p.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min="1"
                  max="31"
                  placeholder="Dia vencimento"
                  value={form[a.id]?.dia_vencimento || ''}
                  onChange={(e) => setCampo(a.id, 'dia_vencimento', e.target.value)}
                  className="h-11 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-white px-3"
                />

                <select
                  value={form[a.id]?.tipo_primeira_cobranca || 'inscricao'}
                  onChange={(e) => setCampo(a.id, 'tipo_primeira_cobranca', e.target.value)}
                  className="h-11 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-white px-3"
                  title="Tipo da primeira cobrança"
                >
                  <option value="inscricao">Inscrição</option>
                  <option value="mensalidade">Mensalidade</option>
                </select>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Valor 1ª cobrança"
                  value={form[a.id]?.valor_primeira_cobranca ?? ''}
                  onChange={(e) => setCampo(a.id, 'valor_primeira_cobranca', e.target.value)}
                  className="h-11 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-white px-3"
                />

                <select
                  value={form[a.id]?.status_primeira_mensalidade || 'aguardando_pagamento'}
                  onChange={(e) => setCampo(a.id, 'status_primeira_mensalidade', e.target.value)}
                  className="h-11 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-white px-3"
                  title="Status da primeira cobrança"
                >
                  <option value="aguardando_pagamento">Aguardando pagamento</option>
                  <option value="pago">Pago</option>
                </select>
              </div>

              <button
                onClick={() => aprovarAluno(a.id)}
                disabled={aprovando === a.id}
                className="bg-[#007AFF] hover:bg-[#0066DD] text-white px-5 h-11 rounded-xl font-bold"
              >
                {aprovando === a.id ? 'Aprovando...' : 'Aprovar aluno'}
              </button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
